import { from, Observable } from "rxjs";
import { PageRequest } from "src/app/support/page-request";
import { Paginated } from "src/app/support/paginated";
import { rxRandomErrorDelayAsync } from "src/app/support/rx-random-error-delay";
import { map, switchMap } from "rxjs/operators";
import { find } from "lodash-es";
import { UserNotify } from "src/app/support/notify-decorator";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { UUID, uuid } from "src/app/support/uuid";
import {
  loadDemoStorage,
  writeDemoStorage
} from "src/app/support/demo-storage";
import { Injectable } from "@angular/core";
import { CostCenterCreate } from "../models/cost-center/cost-center.create";
import { CostCenterDelete } from "../models/cost-center/cost-center.delete";
import { CostCenterLoad } from "../models/cost-center/cost-center.load";
import { CostCenter } from "../models/cost-center/cost-centers";

const DB_NAME = "cost-center";

@Injectable()
export class CostCenterBackend {
  load(request: CostCenterLoad): Observable<CostCenter> {
    return rxRandomErrorDelayAsync(loadDb()).pipe(
      map((list) => find(list, (x) => x.uuid === request.uuid)!)
    );
  }

  list(request: PageRequest): Observable<Paginated<CostCenter>> {
    return rxRandomErrorDelayAsync(loadDb()).pipe(
      map(
        (list) =>
          <Paginated<CostCenter>>{
            list,
            count: list.length
          }
      )
    );
  }

  @UserNotify({
    successMessage: marker("Centro di costo creato"),
    errorMessage: marker("Impossibile creare il centro di costo")
  })
  create(request: CostCenterCreate): Observable<UUID> {
    return rxRandomErrorDelayAsync(loadDb()).pipe(
      switchMap((list: CostCenter[]) => {
        const newUuid = uuid();
        const newCostCenter: Partial<CostCenter> = {
          uuid: newUuid,
          name: request.name
        };

        return from(writeDemoStorage(DB_NAME, [...list, newCostCenter])).pipe(
          map(() => newUuid)
        );
      })
    );
  }

  @UserNotify({
    successMessage: marker("Centro di costo aggiornato"),
    errorMessage: marker("Impossibile aggiornare il centro di costo")
  })
  update(request: CostCenter): Observable<void> {
    return rxRandomErrorDelayAsync(loadDb()).pipe(
      switchMap((list: CostCenter[]) => {
        const newUuid = uuid();
        const newCostCenter: Partial<CostCenter> = {
          uuid: newUuid,
          name: request.name
        };

        return from(
          writeDemoStorage(
            DB_NAME,
            list.map((x) => {
              if (x.uuid === request.uuid) {
                return {
                  ...x,
                  ...request
                };
              }
              return x;
            })
          )
        );
      })
    );
  }

  @UserNotify({
    successMessage: marker("Centro di costo rimosso"),
    errorMessage: marker("Impossibile rimuovere il centro di costo")
  })
  delete(request: CostCenterDelete): Observable<void> {
    return rxRandomErrorDelayAsync(loadDb()).pipe(
      switchMap((list: CostCenter[]) => {
        return from(
          writeDemoStorage(
            DB_NAME,
            list.filter((x) => x.uuid !== request.uuid)
          )
        );
      })
    );
  }
}

async function loadDb(): Promise<CostCenter[]> {
  return (await loadDemoStorage<CostCenter[]>(DB_NAME)) || [];
}
